import { useCallback, useRef, useState } from "react";

import { formatCloudConnectStartError } from "../../lib/cloudConnectError";
import type { CloudRuntimeTerminalFailureResidual } from "../cloudRuntimeTerminalFailureResidual";
import { isCloudRuntimeTerminalFailureResidual } from "../cloudRuntimeTerminalFailureResidualGuard";
import type {
  CloudRuntimeConnectProgressEvent,
  CloudRuntimeConnectStage,
} from "../sidebar/connectCloudRuntime";
import { resolveModelTriggerFallbackLabel } from "./NewThreadLanding.logic";
import { submitNewThread, type SubmitNewThreadInput } from "./NewThreadLanding.submission";
import { resolveDraftSubmissionDecision, type DraftSubmitInput } from "./draftSubmissionDecision";
import { parseStandaloneComposerSlashCommand } from "../../composer-logic";
export type { DraftSubmitInput } from "./draftSubmissionDecision";

/**
 * Stages whose progress summary passes through verbatim. These are the
 * cold-provisioning window (ensure and start), where FOR-3579's composer sat
 * blank for 2m20s. The summary is user-readable ("Ensuring cloud runtime",
 * "Cloud project is still provisioning", ...).
 */
const VERBATIM_PHASE_STAGES: ReadonlySet<CloudRuntimeConnectStage> = new Set([
  "runtime-ensure-request",
  "runtime-ensure-retry",
  "runtime-ensured",
  "runtime-start-request",
  "runtime-started",
]);

/**
 * Stages that get a fixed label instead of the raw progress summary. Raw
 * summaries for these stages ("Requesting cloud runtime connection",
 * "Received runtime connection descriptor", ...) are internal and must never
 * reach the rendered page (regression #15). FOR-3586 added wake/connect
 * visibility: a suspended runtime's connect POST (~32s) now shows a label
 * instead of a bare "Sending".
 */
const FIXED_PHASE_LABELS: ReadonlyMap<CloudRuntimeConnectStage, string> = new Map([
  ["connect-request", "Waking the cloud runtime"],
  ["connect-retry", "Waking the cloud runtime"],
  ["connection-descriptor", "Connecting"],
  ["environment-register", "Connecting"],
  ["environment-register-retry", "Connecting"],
]);

/**
 * The label to show while the connect flow is running, or null when the stage
 * carries no user-meaningful information (clone, connected, ...).
 *
 * Two modes:
 *  - verbatim stages: pass the event's own summary through (ensure/start window)
 *  - fixed-label stages: return a constant string so raw internal summaries
 *    never reach the page (regression #15)
 */
function resolveSendingPhase(event: CloudRuntimeConnectProgressEvent): string | null {
  const fixed = FIXED_PHASE_LABELS.get(event.stage);
  if (fixed !== undefined) return fixed;
  if (!VERBATIM_PHASE_STAGES.has(event.stage)) return null;
  const summary = event.summary.trim();
  return summary.length > 0 ? summary : null;
}

/**
 * Everything the draft needs in order to decide it can send, and to send.
 *
 * Most of it is the submission's own input, reused rather than restated so the
 * two cannot drift. What is added on top are the things the *decision* needs
 * but the submission does not: the worktree mode and branch provenance, which
 * together derive `envMode` and `includeBranch`, and the provider access and
 * inventory, which decide whether a model will be resolvable by the time the
 * turn actually runs.
 */
export interface DraftSubmit {
  /** Whether "no project" is an offer this surface can honour. */
  readonly scratchWorkspaceSupported: boolean;
  readonly canResolveModelOnSubmit: boolean;
  readonly hasSendableContent: boolean;
  /** What to show where a model name would go, when none has resolved. */
  readonly modelTriggerFallbackLabel: string | undefined;
  readonly submitting: boolean;
  readonly submitError: string | null;
  /** The CMMD refusal code behind `submitError`, when the refusal carried one. */
  readonly submitErrorCode: string | null;
  readonly submitFailureResidual: CloudRuntimeTerminalFailureResidual | null;
  /** Local validation feedback that must not be rendered as a runtime failure. */
  readonly localNotice: string | null;
  /**
   * A user-visible label for the current connect-flow phase, or null when
   * nothing is in flight. Replaces a bare "Sending" with what CMMD is doing
   * (FOR-3579: ensure/start window; FOR-3586: connect/wake and register window).
   */
  readonly sendingPhase: string | null;
  readonly submit: () => void;
  readonly onCommandKey: (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => boolean;
}

/**
 * Whether a draft can be sent, and the act of sending it.
 *
 * One hook because the two are the same question asked twice: `submit` refuses
 * for exactly the reasons `hasSendableContent` is false, and splitting them
 * meant the composer could enable its button on one rule while the handler
 * bailed on another.
 */
export function useDraftSubmit(input: DraftSubmitInput): DraftSubmit {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);
  const [submitFailureResidual, setSubmitFailureResidual] =
    useState<CloudRuntimeTerminalFailureResidual | null>(null);
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  const [sendingPhase, setSendingPhase] = useState<string | null>(null);

  const decision = resolveDraftSubmissionDecision(input);
  const { scratchWorkspaceSupported, canResolveModelOnSubmit, hasSendableContent } = decision;
  // This lock changes before React renders the pending state. It is deliberately
  // separate from `submitting`, which only drives the view.
  const submittingRef = useRef(false);

  // A stopped cloud runtime (or an unconnected desktop runtime) exposes no
  // provider inventory yet, so no model label resolves — but submit still
  // works, because the model is picked when the runtime connects. Name that
  // outcome instead of letting the picker fall back to a loading skeleton that
  // never resolves on this surface.
  const modelTriggerFallbackLabel = resolveModelTriggerFallbackLabel({
    modelSelectionResolved: input.selectedModelSelection !== null,
    modelResolvableOnSubmit: canResolveModelOnSubmit,
  });

  /**
   * The latest input, read at click time rather than captured.
   *
   * `input` is a fresh object every render, so depending on it would make the
   * memo below inert — a `useCallback` whose identity changes every render is
   * only a comment claiming otherwise. A submit handler wants the newest values
   * anyway, never the ones that were current when it was last rebuilt, so the
   * ref is the honest shape: one stable `submit` for the life of the hook, and
   * no dependency list to fall out of date.
   */
  const inputRef = useRef(input);
  inputRef.current = input;

  const submit = useCallback(() => {
    const current = inputRef.current;
    if (parseStandaloneComposerSlashCommand(current.prompt) === "usage-limits") {
      setLocalNotice("Usage limits are available in an existing Codex or Claude thread.");
      return;
    }
    const currentDecision = resolveDraftSubmissionDecision(current);
    if (submittingRef.current || !currentDecision.hasSendableContent) return;
    // State updates do not make this ref visible until React renders. Set it
    // before starting the async work so two Enter events in one turn cannot
    // both pass the guard.
    submittingRef.current = true;
    current.onSubmissionStarted?.();
    void runNewThreadSubmission(
      {
        ...currentDecision.submission,
        onProgress: (event) => setSendingPhase(resolveSendingPhase(event)),
      },
      {
        start: () => {
          setSubmitting(true);
          setSubmitError(null);
          setSubmitErrorCode(null);
          setSubmitFailureResidual(null);
          setLocalNotice(null);
          setSendingPhase(null);
        },
        fail: (message, code, failureResidual) => {
          setSubmitError(message);
          setSubmitErrorCode(code);
          setSubmitFailureResidual(failureResidual);
        },
        finish: () => {
          submittingRef.current = false;
          setSubmitting(false);
          setSendingPhase(null);
        },
      },
    );
  }, []);

  // Enter submits, Shift+Enter does not, and this reports whether it consumed
  // the key so the editor falls through for everything else — a handler that
  // swallowed every key would break newlines.
  const onCommandKey = useCallback(
    (key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab", event: KeyboardEvent) => {
      if (key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
        return true;
      }
      return false;
    },
    [submit],
  );

  return {
    scratchWorkspaceSupported,
    canResolveModelOnSubmit,
    hasSendableContent,
    modelTriggerFallbackLabel,
    submitting,
    submitError,
    submitErrorCode,
    submitFailureResidual,
    localNotice,
    sendingPhase,
    submit,
    onCommandKey,
  };
}

interface SubmissionLifecycle {
  readonly start: () => void;
  readonly fail: (
    message: string,
    code: string | null,
    failureResidual: CloudRuntimeTerminalFailureResidual | null,
  ) => void;
  readonly finish: () => void;
}

/**
 * Runs a submission and reports its lifecycle, whatever the outcome.
 *
 * `finally` rather than a call after `await`: a failed submission still has to
 * lower the busy flag, and an early return in the error path is how a draft
 * ended up stuck showing a spinner it could never clear.
 */
async function runNewThreadSubmission(
  input: SubmitNewThreadInput,
  lifecycle: SubmissionLifecycle,
): Promise<void> {
  lifecycle.start();
  try {
    await submitNewThread(input);
  } catch (error: unknown) {
    const failure = readSubmissionError(error);
    lifecycle.fail(
      formatCloudConnectStartError(failure.message),
      failure.code,
      failure.failureResidual,
    );
  } finally {
    lifecycle.finish();
  }
}

/**
 * The message to show and the CMMD code to key on. The code rides on the
 * thrown Error (`throwCloudRuntimeConnectFailure` attaches it); a string or a
 * shapeless rejection carries none.
 */
function readSubmissionError(error: unknown): {
  readonly message: string;
  readonly code: string | null;
  readonly failureResidual: CloudRuntimeTerminalFailureResidual | null;
} {
  if (error instanceof Error) {
    const enriched = error as Error & {
      readonly code?: unknown;
      readonly failureResidual?: unknown;
    };
    const code = enriched.code;
    const failureResidual = isCloudRuntimeTerminalFailureResidual(enriched.failureResidual)
      ? enriched.failureResidual
      : null;
    return {
      message: error.message,
      code: typeof code === "string" ? code : null,
      failureResidual,
    };
  }
  if (typeof error === "string") {
    return { message: error, code: null, failureResidual: null };
  }
  return { message: "Something went wrong. Please try again.", code: null, failureResidual: null };
}
